/**
 * The arithmetic of marking up a snapshot, apart from the DOM so it can be tested and
 * reused wherever a snapshot is shown. Everything is in the snapshot image's own
 * pixels: a mark is in the same place on every screen that shows it.
 */
/** A point on screen (clientX/Y), in the image's pixels, given where the image is drawn. */
export function toImagePoint(client, drawn, image) {
    const x = ((client.x - drawn.left) / Math.max(drawn.width, 1)) * image.width;
    const y = ((client.y - drawn.top) / Math.max(drawn.height, 1)) * image.height;
    return { x: clamp(round(x), 0, image.width), y: clamp(round(y), 0, image.height) };
}
const round = (n) => Math.round(n * 10) / 10;
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
export const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
/**
 * Whether a press and release were a tap rather than a drag: they stayed within a few
 * screen pixels. Measured on screen, because a finger's wobble is the same size on a
 * small snapshot as on a large one.
 */
export const isTap = (start, end, slop = 6) => Math.hypot(start.x - end.x, start.y - end.y) <= slop;
/** The box two corners span, whichever way the drag went. */
export function boxFrom(a, b) {
    return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) };
}
export const contains = (box, p) => p.x >= box.x && p.x <= box.x + box.w && p.y >= box.y && p.y <= box.y + box.h;
/**
 * The element a tap at `p` means: the smallest visible one containing it, since a
 * button inside a card inside a page is what a finger on the button points at.
 * Elements' boxes are in viewport pixels; `scale` takes them to the image's (a
 * screenshot is the viewport times the device's pixel ratio).
 */
export function elementAt(elements, p, scale = 1) {
    if (!elements?.length)
        return undefined;
    let best;
    let bestArea = Infinity;
    elements.forEach((el, i) => {
        const box = scaleBox(el.box, scale);
        if (box.w <= 0 || box.h <= 0 || !contains(box, p))
            return;
        const area = box.w * box.h;
        if (area < bestArea) {
            best = i;
            bestArea = area;
        }
    });
    return best;
}
export const scaleBox = (box, scale) => scale === 1 ? box : { x: box.x * scale, y: box.y * scale, w: box.w * scale, h: box.h * scale };
/**
 * Fewer points for the same line (Ramer–Douglas–Peucker): a pen stroke arrives as a
 * point per pointer event, most of them on the line the others already draw.
 */
export function simplify(points, tolerance = 1.5) {
    if (points.length <= 2)
        return [...points];
    const keep = new Array(points.length).fill(false);
    keep[0] = keep[points.length - 1] = true;
    const stack = [[0, points.length - 1]];
    while (stack.length) {
        const [from, to] = stack.pop();
        let worst = -1;
        let worstAt = -1;
        for (let i = from + 1; i < to; i += 1) {
            const d = offLine(points[i], points[from], points[to]);
            if (d > worst) {
                worst = d;
                worstAt = i;
            }
        }
        if (worst > tolerance && worstAt > 0) {
            keep[worstAt] = true;
            stack.push([from, worstAt], [worstAt, to]);
        }
    }
    return points.filter((_, i) => keep[i]);
}
/** How far `p` is from the line through `a` and `b`. */
function offLine(p, a, b) {
    const len = distance(a, b);
    if (len === 0)
        return distance(p, a);
    return Math.abs((b.x - a.x) * (a.y - p.y) - (a.x - p.x) * (b.y - a.y)) / len;
}
/** Where a mark's number badge goes: at a pin, a region's top-left, a stroke's first point. */
export function anchorOf(geometry) {
    if (geometry.kind === 'pin')
        return geometry.at;
    if (geometry.kind === 'region')
        return { x: geometry.box.x, y: geometry.box.y };
    return geometry.points[0];
}
/** The lines on the page are drawn this thick, so a mark reads the same on a phone screenshot and a 5K one. */
export const strokeWidthFor = (image) => Math.max(2, Math.round(Math.max(image.width, image.height) / 400));
//# sourceMappingURL=geometry.js.map